import React, { useEffect, useMemo, useState } from "react";
import pharmacyLedgerService from "../../Services/pharmacyledger";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const fmt = (n) => {
  const value = Number(n);
  return Number.isFinite(value) ? value.toFixed(2) : "0.00";
};

const number = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const money = (value) => Number(value.toFixed(2));

const withCalculations = (rows) => rows.map((item) => {
  const saleQty = number(item.sale_qty ?? item.total_sale_qty);
  const returnQty = number(item.sale_return_qty);
  const returnAmount = money(number(item.sale_return_value));
  const purchasePrice = number(item.purchase_unit_price);
  const salePrice = number(item.sale_unit_price);
  const grossSales = money(number(item.total_sale ?? item.total_sale_value ?? salePrice * saleQty));
  const netQty = saleQty - returnQty;
  const netCost = money(purchasePrice * netQty);
  const netSales = money(grossSales - returnAmount);

  return {
    ...item,
    sale_qty: saleQty,
    return_qty: returnQty,
    return_amount: returnAmount,
    net_qty: netQty,
    per_piece_purchase_price: purchasePrice,
    per_piece_sale_price: salePrice,
    total_purchase_price: netCost,
    total_sale: grossSales,
    net_sale: netSales,
    profit: money(netSales - netCost),
  };
});

const Profilelosstable = () => {
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    let active = true;
    const fetchLedger = async () => {
      setLoading(true);
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await pharmacyLedgerService.getSummary(params);
      if (!active) return;
      if (response?.success) {
        setLedgerData(Array.isArray(response.data) ? response.data : []);
        setError("");
      } else {
        setLedgerData([]);
        setError("Failed to load pharmacy ledger data.");
      }
      setLoading(false);
    };
    fetchLedger();
    return () => {
      active = false;
    };
  }, [startDate, endDate]);

  const calculatedLedger = useMemo(() => withCalculations(ledgerData), [ledgerData]);

  const totals = useMemo(() => {
    return calculatedLedger.reduce(
      (acc, item) => ({
        sale_qty: acc.sale_qty + item.sale_qty,
        return_qty: acc.return_qty + item.return_qty,
        return_amount: acc.return_amount + item.return_amount,
        total_purchase_price: acc.total_purchase_price + item.total_purchase_price,
        total_sale: acc.total_sale + item.total_sale,
        profit: acc.profit + item.profit,
      }),
      { sale_qty: 0, return_qty: 0, return_amount: 0, total_purchase_price: 0, total_sale: 0, profit: 0 },
    );
  }, [calculatedLedger]);

  const handleDownload = () => {
    if (!calculatedLedger.length) return;
    const wsData = [
      [
        "Medicine",
        "Sale Qty",
        "Return Qty",
        "Per Piece Purchase Price",
        "Total Purchase Price",
        "Per Piece Sale Price",
        "Total Sale",
        "Return Amount",
        "Profit",
      ],
      ...calculatedLedger.map((item) => [
        item.medicine_name,
        item.sale_qty,
        item.return_qty,
        fmt(item.per_piece_purchase_price),
        fmt(item.total_purchase_price),
        fmt(item.per_piece_sale_price),
        fmt(item.total_sale),
        fmt(item.return_amount),
        fmt(item.profit),
      ]),
      [
        "***Total",
        totals.sale_qty,
        totals.return_qty,
        "",
        fmt(totals.total_purchase_price),
        "",
        fmt(totals.total_sale),
        fmt(totals.return_amount),
        fmt(totals.profit),
      ],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");
    saveAs(
      new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], {
        type: "application/octet-stream",
      }),
      `ledger_${Date.now()}.xlsx`,
    );
  };

  const colWidths = ["170px", "90px", "100px", "150px", "150px", "140px", "120px", "130px", "110px"];
  const ColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={{ width: w, minWidth: w }} />
      ))}
    </colgroup>
  );

  const kpiCards = [
    { label: "Sale Qty", value: totals.sale_qty },
    { label: "Purchase Value", value: `Rs.${fmt(totals.total_purchase_price)}` },
    { label: "Sale Value", value: `Rs.${fmt(totals.total_sale)}` },
  ];

  return (
    <div className="p-3 sm:p-4 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
            Sales & Purchase Ledger
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {calculatedLedger.length} records found
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-100"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-100"
            />
          </div>
          <button
            onClick={handleDownload}
            className="text-[13px] border border-gray-200 rounded-lg px-8 py-3 bg-green-500 text-white hover:bg-green-600 focus:outline-none focus:ring-1 focus:ring-blue-100 flex items-center gap-1.5"
          >
            <svg
              className="w-3.5 h-3.5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
              />
            </svg>
            Download
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
        {kpiCards.map(({ label, value }) => (
          <div
            key={label}
            className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5"
          >
            <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
              {label}
            </p>
            <p className="text-base font-semibold text-gray-800">{value}</p>
          </div>
        ))}
        <div className="bg-gray-100 rounded-lg px-3 py-2.5 border-t-2 border-[#F07F1C] col-span-2 sm:col-span-1">
          <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
            Profit
          </p>
          <p className="text-base font-bold text-[#F07F1C]">
            Rs.{fmt(totals.profit)}
          </p>
        </div>
      </div>
      <div className="block sm:hidden space-y-2">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            Loading...
          </div>
        ) : error ? (
          <div className="text-center py-10 text-red-500 text-xs">{error}</div>
        ) : calculatedLedger.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            No ledger data found
          </div>
        ) : (
          <>
            {calculatedLedger.map((item, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl bg-white px-3 py-3"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[11px] font-semibold text-gray-600 uppercase flex-shrink-0">
                    {item.medicine_name?.charAt(0) || "M"}
                  </div>
                  <p className="text-[12px] font-semibold text-gray-800">
                    {item.medicine_name}
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  {[
                    { label: "Sale Qty", value: item.sale_qty },
                    { label: "Return Qty", value: item.return_qty },
                    { label: "Return Amount", value: `Rs.${fmt(item.return_amount)}` },
                    {
                      label: "Per Pc Purchase",
                      value: `Rs.${fmt(item.per_piece_purchase_price)}`,
                    },
                    {
                      label: "Total Purchase Price",
                      value: `Rs.${fmt(item.total_purchase_price)}`,
                    },
                    {
                      label: "Per Pc Sale",
                      value: `Rs.${fmt(item.per_piece_sale_price)}`,
                    },
                    {
                      label: "Total Sale",
                      value: `Rs.${fmt(item.total_sale)}`,
                    },
                    {
                      label: "Profit",
                      value: `Rs.${fmt(item.profit)}`,
                    },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between py-1.5">
                      <span className="text-[11px] text-gray-400 w-28">
                        {label}
                      </span>
                      <span className="text-[11px] text-gray-700 text-right">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="mt-3 bg-[#1F2937] rounded-xl px-3 py-3">
              {[
                { label: "Total Sale Qty", value: totals.sale_qty },
                    { label: "Return Qty", value: totals.return_qty },
                    { label: "Return Amount", value: `Rs.${fmt(totals.return_amount)}` },
                {
                  label: "Total Purchase Price",
                  value: `Rs.${fmt(totals.total_purchase_price)}`,
                },
                {
                  label: "Total Sale",
                  value: `Rs.${fmt(totals.total_sale)}`,
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="flex justify-between py-1.5 border-b border-white/10 last:border-0"
                >
                  <span className="text-[12px] text-gray-400">{label}</span>
                  <span className="text-[12px] text-gray-100 font-medium">
                    {value}
                  </span>
                </div>
              ))}
              <div className="flex justify-between pt-2 mt-1 border-t border-white/20">
                <span className="text-[12px] font-medium text-gray-100">
                  Profit
                </span>
                <span className="text-[14px] font-bold text-[#F07F1C]">
                  Rs.{fmt(totals.profit)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="hidden sm:block border rounded-xl overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full text-sm"
            style={{
              tableLayout: "fixed",
              minWidth: "1160px",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <ColGroup />
            <thead>
              <tr>
                {[
                  "Medicine",
                  "Sale Qty",
                  "Return Qty",
                  "Per Piece Purchase Price",
                  "Total Purchase Price",
                  "Per Piece Sale Price",
                  "Total Sale",
                  "Return Amount",
                  "Profit",
                ].map((label, i) => (
                  <th
                    key={label}
                    className={`sticky top-0 z-20 bg-[#1F2937] text-left px-4 py-2.5 text-[11px] text-gray-100 uppercase font-medium ${i < 8 ? "border-r border-white/10" : ""}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-gray-400 text-[12px]">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-red-500 text-[12px]">
                    {error}
                  </td>
                </tr>
              ) : calculatedLedger.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    No ledger data found
                  </td>
                </tr>
              ) : (
                calculatedLedger.map((item, index) => (
                  <tr
                    key={index}
                    className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${index % 2 ? "bg-gray-50" : "bg-white"}`}
                  >
                    <td className="px-4 py-2.5 border-r border-gray-100">
                      <span className="block whitespace-normal [overflow-wrap:anywhere] text-[12px] font-medium text-gray-800">
                        {item.medicine_name}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      {item.sale_qty}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      {item.return_qty}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      Rs.{fmt(item.per_piece_purchase_price)}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      Rs.{fmt(item.total_purchase_price)}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      Rs.{fmt(item.per_piece_sale_price)}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      Rs.{fmt(item.total_sale)}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      Rs.{fmt(item.return_amount)}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-[12px] font-medium ${item.profit >= 0 ? "text-green-600" : "text-red-600"}`}
                    >
                      Rs.{fmt(item.profit)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="bg-[#1F2937]">
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] font-medium text-gray-100 border-r border-white/10 border-t">
                  Grand Total
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] text-gray-100 border-r border-white/10 border-t">
                  {totals.sale_qty}
                </td>
                    <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] text-gray-100 border-r border-white/10 border-t">
                      {totals.return_qty}
                    </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] text-gray-100 border-r border-white/10 border-t">
                  —
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] text-gray-100 border-r border-white/10 border-t">
                  Rs.{fmt(totals.total_purchase_price)}
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] text-gray-100 border-r border-white/10 border-t">
                  —
                </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] text-gray-100 border-r border-white/10 border-t">
                  Rs.{fmt(totals.total_sale)}
                </td>
                    <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] text-gray-100 border-r border-white/10 border-t">
                      Rs.{fmt(totals.return_amount)}
                    </td>
                <td className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] font-semibold text-[#F07F1C] border-t border-white/10">
                  Rs.{fmt(totals.profit)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Profilelosstable;
